import { createContext, useContext, type ReactNode } from 'react';
import { localSite } from '../data/site';
import { pages as localPages } from '../data/pages';
import type { SiteSettings } from '../core/types/site';
import type { PageContent } from '../core/types/pages';

/**
 * De instellingen (salon, voorwaarden, cadeaustaffel, codes, creators…) en de paginateksten als
 * echte React-state (docs/ARCHITECTUUR.md §6). `CatalogProvider` laadt ze en geeft een nieuwe
 * stand door zodra de server antwoordt of na verversen; wie `useSite()` of `usePages()` leest,
 * tekent dan vanzelf opnieuw.
 *
 * Overgang: `applySiteSettings()`/`applyPageContent()` werken de gedeelde objecten in `data/*`
 * voorlopig óók nog bij, zodat code die nog niet is omgezet (en code buiten React, zoals SEO en
 * huidtips) blijft werken. Nieuwe schermen lezen alleen deze hooks en geven waarden als parameter
 * aan pure functies (`tierIn`, `unlockedIn`, `codesIn`, `freeShippingLine`).
 * Onderdelen aan/uit: altijd via `useFeatures()`.
 */
const SiteContext = createContext<SiteSettings | null>(null);
const PagesContext = createContext<PageContent | null>(null);
export function SiteProvider({
  site,
  pages,
  children
}: {
  site: SiteSettings;
  pages: PageContent;
  children: ReactNode;
}) {
  return <SiteContext.Provider value={site} data-id="element-5510" data-mp="1t769gcnaohkk">
      <PagesContext.Provider value={pages} data-id="element-5511" data-mp="1x76lxrqu2ioz">{children}</PagesContext.Provider>
    </SiteContext.Provider>;
}
let fallback: SiteSettings | null = null;

/** De actuele instellingen. Buiten de provider (losse tests, beheer-preview): de lokale stand. */
export function useSite(): SiteSettings {
  const ctx = useContext(SiteContext);
  if (ctx) return ctx;
  fallback ??= localSite();
  return fallback;
}

/** De actuele paginateksten. Buiten de provider: de lokale teksten. */
export function usePages(): PageContent {
  return useContext(PagesContext) ?? localPages;
}